import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Search, MessageCircle } from 'lucide-react';
export function FAQPage() {
  const [activeCategory, setActiveCategory] = useState('Program Structure');
  const [openItem, setOpenItem] = useState<number | null>(1);
  const categories = ['Program Structure', 'Advisor Engagement', 'Funding & Support', 'Platform Usage'];
  const faqs = [{
    id: 1,
    category: 'Program Structure',
    question: 'What is the expected time commitment for the accelerator?',
    answer: 'Founders should expect to commit approximately 5-8 hours per week. This includes 1 hour of advisory sessions, 2-3 hours of curriculum review and assignments, and 2-4 hours of community events or workshops. The program is designed to integrate with your business operations, not distract from them.'
  }, {
    id: 2,
    category: 'Program Structure',
    question: 'Are the weekly workshops mandatory?',
    answer: 'While we highly encourage attendance at all live workshops to maximize peer learning and networking, we understand founders have demanding schedules. Core curriculum workshops are recorded and available in the Courses section within 24 hours. However, you must attend at least 75% of live sessions to graduate.'
  }, {
    id: 3,
    category: 'Program Structure',
    question: 'How is the Demo Day structured?',
    answer: 'Demo Day occurs in Month 6. It is a hybrid event where founders pitch to a curated audience of investors, corporate partners, and BOF stakeholders. You will receive extensive pitch preparation in Month 5, including slide design support and narrative coaching.'
  }, {
    id: 4,
    category: 'Advisor Engagement',
    question: 'How often can I meet with my lead advisor?',
    answer: 'You are allocated two 45-minute sessions per month with your lead advisor. You can schedule these via the Advisory tab. If urgent strategic issues arise, you can request an ad-hoc 15-minute sync.'
  }, {
    id: 5,
    category: 'Advisor Engagement',
    question: 'Can I connect with advisors other than my lead?',
    answer: 'Yes. During Month 3 and Month 5, we host "Expert Office Hours" where you can book sessions with subject matter experts across the BOF network for specific challenges (e.g., legal, specialized marketing, technical architecture).'
  }];
  const filteredFaqs = faqs.filter(faq => faq.category === activeCategory);
  return <div className="space-y-8 animate-in fade-in duration-500 max-w-5xl mx-auto" data-id="element-528">
      <div className="text-center mb-12" data-id="element-529">
        <h1 className="text-3xl font-heading font-bold text-catalyst-black tracking-tight mb-4" data-id="element-530">
          Frequently Asked Questions
        </h1>
        <div className="relative max-w-xl mx-auto" data-id="element-531">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} data-id="element-532" />
          <input type="text" placeholder="Search for answers..." className="w-full pl-12 pr-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-catalyst-mustard/50 focus:border-catalyst-mustard transition-all text-base shadow-sm" data-id="element-533" />
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-8" data-id="element-534">
        {/* Categories Sidebar */}
        <div className="w-full md:w-64 shrink-0" data-id="element-535">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-2 sticky top-24" data-id="element-536">
            {categories.map(category => <button key={category} onClick={() => {
            setActiveCategory(category);
            setOpenItem(null);
          }} className={`w-full text-left px-4 py-3 rounded-lg text-sm font-medium transition-all ${activeCategory === category ? 'bg-catalyst-mustard/10 text-catalyst-mustard-dark border border-catalyst-mustard/20' : 'text-gray-600 hover:bg-gray-50 border border-transparent'}`} data-id="element-537">
                {category}
              </button>)}
          </div>

          <div className="mt-6 bg-catalyst-black text-white rounded-xl p-6 text-center" data-id="element-538">
            <MessageCircle className="mx-auto mb-3 text-catalyst-mustard" size={32} data-id="element-539" />
            <h3 className="font-bold mb-2" data-id="element-540">Still have questions?</h3>
            <p className="text-sm text-gray-400 mb-4" data-id="element-541">
              Our program team is here to help you navigate the accelerator.
            </p>
            <button className="w-full bg-catalyst-mustard text-catalyst-black py-2 rounded-lg text-sm font-semibold hover:bg-catalyst-mustard-light transition-colors" data-id="element-542">
              Contact Support
            </button>
          </div>
        </div>

        {/* FAQ Content */}
        <div className="flex-1" data-id="element-543">
          <h2 className="text-2xl font-bold text-catalyst-black mb-6" data-id="element-544">
            {activeCategory}
          </h2>

          <div className="space-y-4" data-id="element-545">
            {filteredFaqs.length > 0 ? filteredFaqs.map(faq => <div key={faq.id} className={`bg-white rounded-xl border transition-all duration-200 overflow-hidden ${openItem === faq.id ? 'border-catalyst-mustard shadow-md' : 'border-gray-200 shadow-sm hover:border-gray-300'}`} data-id="element-546">
                  <button onClick={() => setOpenItem(openItem === faq.id ? null : faq.id)} className="w-full flex justify-between items-center p-6 text-left focus:outline-none" data-id="element-547">
                    <span className={`font-semibold pr-8 ${openItem === faq.id ? 'text-catalyst-mustard-dark' : 'text-catalyst-black'}`} data-id="element-548">
                      {faq.question}
                    </span>
                    {openItem === faq.id ? <ChevronUp className="text-catalyst-mustard shrink-0" size={20} data-id="element-549" /> : <ChevronDown className="text-gray-400 shrink-0" size={20} data-id="element-550" />}
                  </button>

                  <div className={`px-6 overflow-hidden transition-all duration-300 ease-in-out ${openItem === faq.id ? 'max-h-96 pb-6 opacity-100' : 'max-h-0 opacity-0'}`} data-id="element-551">
                    <div className="w-full h-px bg-gray-100 mb-4" data-id="element-552"></div>
                    <p className="text-gray-600 leading-relaxed text-sm" data-id="element-553">
                      {faq.answer}
                    </p>
                  </div>
                </div>) : <div className="text-center py-12 bg-white rounded-xl border border-gray-200 border-dashed" data-id="element-554">
                <p className="text-gray-500" data-id="element-555">
                  No questions found for this category yet.
                </p>
              </div>}
          </div>
        </div>
      </div>
    </div>;
}